/**
 * Client-side mirror of the backend's JSON merge patch
 * (services/conversation-service/app/campaign_state.py::json_merge_patch).
 *
 * RFC 7396 semantics plus the same id-merge extension: lists whose elements
 * all carry an `id` are merged element-wise by id instead of replaced, so a
 * patch regenerating one creative concept leaves the others untouched.
 *
 * Used ONLY for optimistic application of `canvas` SSE patches between
 * server fetches — the server projection remains authoritative and the hook
 * re-fetches after every turn.
 */

type Json = unknown;

function isPlainObject(v: Json): v is Record<string, Json> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isIdList(v: Json): v is Array<Record<string, Json>> {
  return (
    Array.isArray(v) &&
    v.length > 0 &&
    v.every((item) => isPlainObject(item) && 'id' in item)
  );
}

function mergeIdLists(
  target: Array<Record<string, Json>>,
  patch: Array<Record<string, Json>>,
): Array<Record<string, Json>> {
  const indexById = new Map<Json, number>();
  target.forEach((item, i) => indexById.set(item.id, i));
  const result = [...target];
  for (const patchItem of patch) {
    const i = indexById.get(patchItem.id);
    if (i !== undefined) {
      result[i] = jsonMergePatch(result[i], patchItem) as Record<string, Json>;
    } else {
      result.push(patchItem);
    }
  }
  return result;
}

// Keys whose list values merge element-wise by id instead of being replaced —
// mirrors ID_MERGE_KEYS in the backend reducer. Lists like `variations` (whose
// takes also carry ids) must REPLACE wholesale on regeneration/undo.
const ID_MERGE_KEYS = new Set(['creativeSet', 'renditions']);

export function jsonMergePatch(target: Json, patch: Json, key?: string): Json {
  if (!isPlainObject(patch)) {
    if (key !== undefined && ID_MERGE_KEYS.has(key) && isIdList(target) && isIdList(patch)) {
      return mergeIdLists(target, patch);
    }
    return patch;
  }

  const base: Record<string, Json> = isPlainObject(target) ? { ...target } : {};
  for (const [k, value] of Object.entries(patch)) {
    if (value === null) {
      delete base[k];
    } else {
      base[k] = jsonMergePatch(base[k], value, k);
    }
  }
  return base;
}

/**
 * Collect dotted paths of leaf fields a patch touches, for flash highlights.
 * (e.g. {budget:{split:{meta:40}}} -> ["budget.split.meta", "budget.split"]).
 * Id-list entries contribute their list path (e.g. "creativeSet.c2").
 */
export function collectPatchPaths(patch: Json, prefix = ''): string[] {
  const paths: string[] = [];
  if (isIdList(patch)) {
    for (const item of patch as Array<Record<string, Json>>) {
      paths.push(prefix ? `${prefix}.${String(item.id)}` : String(item.id));
    }
    if (prefix) paths.push(prefix);
    return paths;
  }
  if (!isPlainObject(patch)) {
    return prefix ? [prefix] : [];
  }
  if (prefix) paths.push(prefix);
  for (const [key, value] of Object.entries(patch)) {
    const next = prefix ? `${prefix}.${key}` : key;
    paths.push(...collectPatchPaths(value, next));
  }
  return paths;
}
